import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Select, TextField, Button, Spinner, Alert, Chip, EmptyState } from '@glplus/glpweb-dsl';
import { adminApiClient, AdminApiError, type VendorLocationItem } from '../../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../../lib/buttonStyles';
import { Actions, Card, Field, Panel, PRIMARY_BTN, SideBySide, day, MUTED } from './parts';

/**
 * A vendor's new or changed location, waiting for a person (§15i). Until it is
 * approved shoppers see the store exactly as before; approving makes the
 * vendor's details what they see and publishes the prices saved meanwhile.
 */

const FNS_OPTIONS = [
  { value: 'unchecked', label: 'Not checked yet' },
  { value: 'matches', label: 'Matches the USDA retailer locator' },
  { value: 'no_match', label: 'Does not match' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function fnsDecision(
  fnsNumber: string | null,
  answer: string,
  sourceUrl: string,
): { fnsVerified: boolean; fnsSourceUrl: string } | Record<string, never> {
  return fnsNumber && answer !== 'unchecked'
    ? { fnsVerified: answer === 'matches', fnsSourceUrl: sourceUrl.trim() }
    : {};
}

export function needsFnsSource(fnsNumber: string | null, answer: string, sourceUrl: string): boolean {
  return Boolean(fnsNumber && answer !== 'unchecked' && !sourceUrl.trim());
}

function hoursText(hours: Record<string, string> | null): string | null {
  if (!hours) return null;
  const lines = DAYS.filter((d) => hours[d]).map((d) => `${d.slice(0, 3)} ${hours[d].replace(/^(\d{2})(\d{2})-(\d{2})(\d{2})$/, '$1:$2–$3:$4')}`);
  return lines.length > 0 ? lines.join('\n') : null;
}

export function LocationsTab({ canReview, onCount }: { canReview: boolean; onCount: (n: number) => void }) {
  const [items, setItems] = useState<VendorLocationItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminApiClient.listVendorLocations('verifying');
      setItems(res.items);
      onCount(res.counts.verifying ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load locations');
    }
  }, [onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!items)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Spinner color="brand" />
      </Box>
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {flash && <Alert severity="success">{flash}</Alert>}
      {items.length === 0 ? (
        <EmptyState icon="storefront" title="No locations waiting" body="A location a vendor adds or changes shows here until someone checks it." />
      ) : (
        items.map((row) => (
          <LocationCard
            key={row.storeId}
            row={row}
            canReview={canReview}
            onDone={(msg) => {
              setFlash(msg);
              void load();
            }}
          />
        ))
      )}
    </Box>
  );
}

function matchLine(row: VendorLocationItem): string {
  const m = row.match;
  const away = m.distanceM != null ? ` (${Math.round(m.distanceM)} m away)` : '';
  if (m.kind === 'existing_store') {
    return `Matched to a store ${m.savedByShoppers} shopper${m.savedByShoppers === 1 ? ' has' : 's have'} saved${away}.`;
  }
  if (m.kind === 'poi') return `Matched to the store finder entry “${m.poi?.name ?? 'unnamed'}”${m.poi?.address ? ` — ${m.poi.address}` : ''}${away}.`;
  return 'No existing store matched — this is a new store.';
}

function LocationCard({ row, canReview, onDone }: { row: VendorLocationItem; canReview: boolean; onDone: (msg: string) => void }) {
  const [open, setOpen] = useState<'changes' | null>(null);
  const [note, setNote] = useState('');
  const [fns, setFns] = useState('unchecked');
  const [fnsSourceUrl, setFnsSourceUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sent = row.submitted;
  const name = sent?.name ?? row.name;
  const attached = row.match.kind === 'existing_store' || row.match.kind === 'poi';

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onDone(done);
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };
  const fnsAnswer = fnsDecision(row.fnsNumber, fns, fnsSourceUrl);
  const missingFnsSource = needsFnsSource(row.fnsNumber, fns, fnsSourceUrl);

  return (
    <Card testId={`vendor-location-${row.storeId}`}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>
          {name}
        </Typography>
        <Chip label={row.vendor?.businessName ?? 'Unknown vendor'} size="small" />
        <Chip label={row.vendorSource === 'import' ? 'From a file' : 'Typed in'} size="small" />
      </Box>
      <Typography sx={{ fontSize: 13, color: MUTED }}>Sent {day(row.vendorSubmittedAt)}</Typography>
      <Typography sx={{ mt: 1, fontSize: 14 }} data-testid="vendor-location-match">
        {matchLine(row)}
      </Typography>

      <SideBySide
        leftTitle="What the vendor sent"
        rightTitle={attached ? 'What shoppers see today' : 'What shoppers see today — nothing yet'}
        left={
          <>
            <Field label="Name">{sent?.name ?? row.name}</Field>
            <Field label="Address">{sent?.formattedAddress ?? row.formattedAddress}</Field>
            <Field label="Phone">{sent?.phoneE164 ?? row.phoneInternational}</Field>
            <Field label="Website">{sent?.website ?? row.website}</Field>
            <Field label="Hours">{hoursText(sent?.openingHours ?? null)}</Field>
            <Field label="Pickup / delivery">{sent ? `${sent.pickupSupported ? 'Pickup' : 'No pickup'} · ${sent.deliverySupported ? 'Delivery' : 'No delivery'}` : null}</Field>
          </>
        }
        right={
          attached ? (
            <>
              <Field label="Name">{row.name}</Field>
              <Field label="Address">{row.formattedAddress}</Field>
              <Field label="Phone">{row.phoneInternational}</Field>
              <Field label="Website">{row.website}</Field>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: MUTED }}>The store appears to shoppers once this location is approved.</Typography>
          )
        }
      />

      {row.fnsNumber && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Field label="SNAP (FNS) number">{row.fnsNumber}</Field>
          {canReview && (
            <>
              <Select ariaLabel="SNAP number check" label="SNAP number check" options={FNS_OPTIONS} value={fns} onChange={(_e, v) => setFns(String(v))} size="small" native sx={{ width: 300 }} />
              {fns !== 'unchecked' && (
                <TextField
                  label="Public FNS source URL"
                  ariaLabel="Public source used to check the FNS number"
                  value={fnsSourceUrl}
                  onChange={(e) => setFnsSourceUrl(e.target.value)}
                  type="url"
                  placeholder="https://www.fns.usda.gov/…"
                  helperText="Record the public source used for this result."
                  sx={{ minWidth: 360 }}
                />
              )}
            </>
          )}
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {canReview && (
        <Actions>
          <Button
            ariaLabel={`Approve ${name}`}
            buttonText={busy ? 'Working…' : 'Approve'}
            variant="contained"
            size="small"
            disabled={busy || missingFnsSource}
            sx={PRIMARY_BTN}
            dataTestId="vendor-location-approve"
            onClick={() => void run(() => adminApiClient.decideVendorLocation(row.storeId, { decision: 'live', ...fnsAnswer }), `${name} is live. Its prices are published.`)}
          />
          <Button ariaLabel={`Ask for changes to ${name}`} buttonText="Needs changes" variant="outlined" size="small" disabled={busy} sx={NEUTRAL_BTN} dataTestId="vendor-location-changes" onClick={() => setOpen(open ? null : 'changes')} />
          {attached && (
            <Button
              ariaLabel={`${name} is not the store we matched`}
              buttonText="Not the store we matched"
              variant="outlined"
              size="small"
              disabled={busy}
              sx={DANGER_BTN}
              dataTestId="vendor-location-separate"
              onClick={() => void run(() => adminApiClient.separateVendorLocation(row.storeId), `${name} is now its own store, still waiting for a decision.`)}
            />
          )}
        </Actions>
      )}

      {open === 'changes' && (
        <Panel title="What does the vendor need to change?">
          <TextField label="The vendor reads this" ariaLabel="What the vendor needs to change" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} dataTestId="vendor-location-note" />
          <Box sx={{ mt: 1.5 }}>
            <Button
              ariaLabel="Send back to the vendor"
              buttonText="Send back"
              variant="contained"
              size="small"
              disabled={busy || !note.trim() || missingFnsSource}
              sx={PRIMARY_BTN}
              dataTestId="vendor-location-send-back"
              onClick={() => void run(() => adminApiClient.decideVendorLocation(row.storeId, { decision: 'needs_changes', note: note.trim(), ...fnsAnswer }), `Sent back to ${row.vendor?.businessName ?? 'the vendor'} with your note.`)}
            />
          </Box>
        </Panel>
      )}
    </Card>
  );
}
